import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {v4} from 'uuid';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {BootstrapMinMediumMediaQuery} from 'app/general/mediaqueries';
import {useWsOpenaiMessage} from 'app/openai/hooks';
import {ToggleButton} from 'app/procedures/components/ProcedureDiagram/ToggleButton';
import {useAccountSettingsState} from 'app/settings/api';
import {useToggleWidgetIsOpen, useWidgetIsOpenState} from 'app/settings/hooks';
import {SWEETAI_CHAT_WIDGET} from 'app/settings/slice';
import {getContentType} from 'app/utils/content-type';
import {getObjectName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';

import {MessageExchange} from './MessagePanel';
import {PLACEHOLDERS, PROMPTS} from './utils';

import {DataSharingEnabledGate} from '../DataSharingEnabledGate';
import {PermitDataGatheringInfoModal} from '../PermitDataGatheringInfoModal';

import type {StreamedAICommandType} from 'app/openai/types';
import type {
  DetailPolicyVersionSerializedType,
  ProcedureVersionSerializedType,
} from 'sweet-types';

type AIEditableDocument =
  | Pick<
      ProcedureVersionSerializedType,
      'id' | 'content_type' | 'is_large' | 'version_object_id'
    >
  | Pick<
      DetailPolicyVersionSerializedType,
      'id' | 'content_type' | 'is_large' | 'version_object_id'
    >;

interface Props {
  title: ReturnType<typeof getObjectName>;
  document: AIEditableDocument;
  onCommands?: (commands: Array<StreamedAICommandType>) => unknown;
  chatType: 'edit_procedure_chat' | 'edit_policy_chat';
  initialThreadId?: string;
}

const versionToBaseClassMapper = (
  versionContentType:
    | ProcedureVersionSerializedType['content_type']
    | DetailPolicyVersionSerializedType['content_type']
) => {
  const mapper: {procedureversion: 'procedure'; policyversion: 'policy'} = {
    procedureversion: 'procedure',
    policyversion: 'policy',
  };

  return mapper[versionContentType] || 'procedure';
};

export const EditChatBox = ({
  title,
  document,
  onCommands,
  chatType,
  initialThreadId,
}: Props) => {
  const toggleWidgetIsOpen = useToggleWidgetIsOpen();
  const isShowingChat = useWidgetIsOpenState({
    contentType: versionToBaseClassMapper(document.content_type),
    objectId: document.version_object_id,
    widgetName: SWEETAI_CHAT_WIDGET,
  });
  const toggleWidget = useCallback(() => {
    return toggleWidgetIsOpen({
      contentType: versionToBaseClassMapper(document.content_type),
      objectId: document.version_object_id,
      widgetName: SWEETAI_CHAT_WIDGET,
    });
  }, [document]);
  const [thread_id, setThreadId] = useState<string>(initialThreadId || v4());

  const contentType = getContentType(
    document?.content_type ?? 'procedureversion'
  );
  const randomSeed = useMemo(Math.random, []);

  const {isLoading, commands} = useWsOpenaiMessage({
    type: chatType,
    object_id: document.id,
    content_type: document.content_type,
    thread_id,
  });

  useEffect(() => {
    onCommands?.(commands);
  }, [commands]);

  useEffect(() => {
    // isLoading is null in the beginning, and we want it to be ``false``.
    if (thread_id === initialThreadId && isLoading === false) {
      // forces a threadId refresh after initial doc generation is complete.
      // this is so the AI doesn't continue to see old discussion in its
      // history that migh derail its context.
      if (thread_id === initialThreadId) {
        setThreadId(v4());
      }
    }
  }, [isLoading]);

  const {require_ai_data_sharing_at} = useAccountSettingsState().data!;

  if (require_ai_data_sharing_at == null) {
    return <FakeSweetAISideButton />;
  }
  return (
    <>
      {isShowingChat ? (
        <MessageExchange
          threadId={thread_id}
          chatType={chatType}
          object_id={document.id}
          content_type={document.content_type}
          initialAIMessage={`Hello! I'm here to help you edit this ${contentType.name} "${title}". Let's work together to make it perfect. How can I assist you today?`}
          placeholderMessage={
            PLACEHOLDERS[chatType][
              Math.floor(randomSeed * PLACEHOLDERS[chatType].length)
            ]
          }
          placeholder={gt('Ask about editing this %s', contentType.name)}
          toggleMessagePanel={toggleWidget}
          prompts={PROMPTS[chatType]}
        />
      ) : (
        <ToggleWidgetDesktopButton toggleWidget={toggleWidget} />
      )}
    </>
  );
};

const FakeSweetAISideButton = () => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  return (
    <>
      <PermitDataGatheringInfoModal
        isOpen={isEditModalOpen}
        toggleModal={() => setIsEditModalOpen(false)}
      />
      <ToggleWidgetDesktopButton
        toggleWidget={() => setIsEditModalOpen(true)}
      />
    </>
  );
};

export const EditChatBoxPermissionWrapper = (props: Props) => {
  if (props.document?.is_large) {
    return null;
  }

  const isAlreadyChatting = props.initialThreadId != null;
  if (isAlreadyChatting) {
    // Allow customer to continue chat that already had begun despite no account permissions.
    return <EditChatBox {...props} />;
  }

  return (
    <DataSharingEnabledGate
      sharingEnabledElement={<EditChatBox {...props} />}
      sharingDisabledElement={<FakeSweetAISideButton />}
    />
  );
};

type ButtonProps = {
  toggleWidget: () => void;
};

export const ToggleWidgetDesktopButton = ({toggleWidget}: ButtonProps) => (
  <BootstrapMinMediumMediaQuery className="d-print-none">
    <LazyTooltip tooltip={gt('Chat with SweetAI')} placement="left">
      <ToggleButton
        deltaDistance={-10}
        position="top"
        showingDiagram={false}
        toggleDiagram={toggleWidget}
        iconName={'fa-robot'}
      />
    </LazyTooltip>
  </BootstrapMinMediumMediaQuery>
);
